/// <amd-module name="Repos/Tfvc/Views/ChangesetsHub/Components/ChangesetsHub" />
import * as React from "react";
import { TfvcVersionDescriptor } from "Repos/Common/Contracts/Git";
import { tfvcVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { getReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { ChangesetsHistoryView } from "Repos/Tfvc/ChangesetsList/Components/ChangesetsHistoryView";
import { getTfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import * as Resources from "Repos/Tfvc/Views/ChangesetsHub/Resources";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TextField, TextFieldFocusTreatmentBehavior, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getTfvcClient } from "../RestClient/Tfvc";

class ChangesetsHub extends VssComponent {
    private filterVisible: ObservableValue<boolean>;
    private path: string;
    private version: TfvcVersionDescriptor;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        const historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        this.filterVisible = new ObservableValue<boolean>(
            Boolean(state["toVersion"] || state["fromVersion"] || state["toDate"] || state["fromDate"] || state["user"])
        );

        const project = getTFSProject(context.pageContext);
        const rootPath = "$/" + (project ? project.name : "");
        this.path = state.itemPath || rootPath;
        this.version = tfvcVersionStringToVersionDescriptor(state.itemVersion || "T");

        if (this.path !== rootPath) {
            getReposMessagesService(context.pageContext).showMessage({
                buttons: [
                    {
                        id: "show-root",
                        onClick: () => {
                            const url = getTfvcRoutingService(context.pageContext).getChangesetsHubUrl(project ? project.name : "");
                            FastPageSwitch(context.pageContext, url, true);
                        },
                        text: Resources.ShowRoot
                    }
                ],
                renderMessage: () => <span>{format(Resources.FilteredPathMessage, this.path)}</span>,
                severity: MessageCardSeverity.Info
            });
        }
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;
                        const commandBarItems: IHeaderCommandBarItem[] = [
                            {
                                ariaLabel: Resources.ShowFilter,
                                iconProps: { iconName: "Filter" },
                                id: "filter",
                                onActivate: () => (this.filterVisible.value = !this.filterVisible.value),
                                subtle: true
                            }
                        ];

                        return (
                            <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                                <CustomHeader className="repos-commits-hub-header">
                                    <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch">
                                        <HeaderTitleArea>
                                            <HeaderTitleRow>
                                                <HeaderTitle titleSize={TitleSize.Large}>{Resources.ChangesetsPageHeader}</HeaderTitle>
                                            </HeaderTitleRow>
                                        </HeaderTitleArea>
                                        <HeaderCommandBar items={commandBarItems} />
                                        {!isExtraSmallWidth && (
                                            <div className="flex-row margin-left-8" role="search">
                                                <ChangesetSearchTextField />
                                            </div>
                                        )}
                                    </div>
                                </CustomHeader>
                                <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                                    <ReposMessages className="flex-noshrink" showPullRequestSuggestions={false} />
                                    <ChangesetsHistoryView
                                        filterVisible={this.filterVisible}
                                        shouldUpdateUrl={true}
                                        showHeader={false}
                                        filePath={this.path}
                                        version={this.version}
                                    />
                                </div>
                            </Page>
                        );
                    }}
                </ScreenSizeObserver>
            </Surface>
        );
    }
}

function ChangesetSearchTextField() {
    const { pageContext } = useComponentContext();
    const searchValue = new ObservableValue("");
    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
                doSearch();
                break;

            case KeyCode.escape:
                searchValue.value = "";
                break;

            default:
                return;
        }

        // We only get here if the keypress has been handled.
        ev.preventDefault();
        ev.stopPropagation();
    };

    const doSearch = () => {
        const changesetId = parseInt(searchValue.value);
        if (!changesetId) {
            return;
        }

        getTfvcClient(pageContext)
            .getChangeset(changesetId)
            .then(
                changeset => {
                    if (changeset) {
                        const url = getTfvcRoutingService(pageContext).getChangesetDetailsUrl(changesetId);
                        FastPageSwitch(pageContext, url, true);
                    }
                },
                (error: Error) => {
                    getReposMessagesService(pageContext).showMessage({
                        renderMessage: () => <span>{error.message}</span>,
                        severity: MessageCardSeverity.Warning
                    });
                }
            );
    };

    return (
        <TextField
            ariaLabel={Resources.SearchChangesetId}
            className="bolt-text-filterbaritem flex-grow"
            containerClassName="flex-grow"
            inputClassName="bolt-text-filterbaritem-input"
            focusTreatment={TextFieldFocusTreatmentBehavior.none}
            onKeyDown={onKeyDown}
            onChange={(event, value) => (searchValue.value = value)}
            placeholder={Resources.SearchChangesetId}
            style={TextFieldStyle.inline}
            suffixIconProps={{ iconName: "Search", onClick: doSearch }}
            value={searchValue}
            width={TextFieldWidth.tabBar}
            maxLength={10}
        />
    );
}

Components.add("ms.vss-code-web.tfvc-changesets-hub", ChangesetsHub);
